import { Popover } from '@headlessui/react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import Menu from 'components/Menu';
import { LANGUAGES } from 'domains/language';
import { useTrack } from 'utils/hooks/useMixPanel';
import { CaretDown } from '@phosphor-icons/react';
import { Button } from '@expressable/syntax-ds';

type Props = {
  className?: string;
  position?: 'left' | 'right';
};

export const LanguageSwitcher: React.FC<Props> = ({ className, position = 'right' }) => {
  const {
    i18n: { language, changeLanguage },
    t,
  } = useTranslation();
  const { track } = useTrack();

  const onSwitchLanguage = (langId: string) => {
    track('Language Changed', { language: langId });
    changeLanguage(langId);
  };

  const currentLanguage = LANGUAGES.find(({ value }) => value === language);

  return (
    <Popover className={classNames('relative', className)}>
      {({ open }) => {
        return (
          <>
            <Popover.Button>
              <Button
                as="span"
                variant="secondary"
                size="sm"
                className={classNames({
                  'outline-2 outline-black-100% outline-offset-0': open,
                })}
              >
                {currentLanguage?.abbreviation}
                <CaretDown
                  className={classNames('size-e16 transition-transform', {
                    'rotate-180': open,
                  })}
                />
              </Button>
            </Popover.Button>
            <Popover.Panel
              className={classNames(
                'absolute z-50 w-[360px] max-w-[90vw] mt-10',
                {
                  'right-0': position === 'left',
                },
                'max-h-[380px] overflow-y-auto border border-gray-400 rounded-lg',
              )}
            >
              <Menu>
                <Menu.SectionTitle>{t('language.sectionTitle')}</Menu.SectionTitle>
                <Menu.SelectSection
                  defaultValue={language}
                  onChange={(value: string) => {
                    onSwitchLanguage(value);
                  }}
                >
                  {LANGUAGES.map(({ value, label }) => (
                    <Menu.SelectItem value={value} key={value}>
                      {label}
                    </Menu.SelectItem>
                  ))}
                </Menu.SelectSection>
              </Menu>
            </Popover.Panel>
          </>
        );
      }}
    </Popover>
  );
};
